import { useState, useEffect } from 'react';
import { getAIAgentStats, AIAgentStats } from '../../lib/ai-agent-api';
import { motion } from 'motion/react';
import { 
  Sparkles, 
  Bot,
  MessageSquare,
  Settings,
  Zap,
  Clock,
  TrendingUp,
  Users,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

export function AIAgent() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [agentName, setAgentName] = useState('PropertyBot');
  const [greeting, setGreeting] = useState('Hello! I\'m here to help you find your perfect property. How can I assist you today?');
  const [responseTime, setResponseTime] = useState('instant');
  const [leadQualification, setLeadQualification] = useState(true);
  const [autoFollowUp, setAutoFollowUp] = useState(true);
  const [businessHoursOnly, setBusinessHoursOnly] = useState(false);
  const [stats, setStats] = useState<AIAgentStats>({
    totalConversations: 0,
    leadsQualified: 0,
    avgResponseTime: '—',
    satisfactionRate: 0,
    recentConversations: [],
  });

  useEffect(() => {
    getAIAgentStats().then(setStats);
  }, []);

  const recentConversations = stats.recentConversations;

  const handleSaveSettings = () => {
    toast.success('AI Agent settings saved successfully');
  };

  const handleToggleAgent = (enabled: boolean) => {
    setIsEnabled(enabled);
    if (enabled) {
      toast.success('AI Agent activated');
    } else {
      toast.info('AI Agent deactivated');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Property Assistant AI Agent
          </h2>
          <p className="text-muted-foreground mt-1">
            24/7 AI-powered assistant for property inquiries and lead qualification
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={isEnabled ? 'bg-green-500' : 'bg-gray-500'}>
            {isEnabled ? 'Active' : 'Inactive'}
          </Badge>
          <Switch
            checked={isEnabled}
            onCheckedChange={handleToggleAgent}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Conversations</p>
                <p className="text-2xl font-bold">{stats.totalConversations}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Leads Qualified</p>
                <p className="text-2xl font-bold">{stats.leadsQualified}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Response Time</p>
                <p className="text-2xl font-bold">{stats.avgResponseTime}</p>
              </div>
              <Zap className="w-8 h-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Satisfaction Rate</p>
                <p className="text-2xl font-bold" data-testid="marketing-ai-satisfaction-rate">{stats.satisfactionRate}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Agent Configuration
            </CardTitle>
            <CardDescription>
              Customize your AI agent's behavior and responses
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="agentName">Agent Name</Label>
              <Input
                id="agentName"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="e.g., PropertyBot"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="greeting">Welcome Greeting</Label>
              <Textarea
                id="greeting"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder="Enter the greeting message..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responseTime">Response Style</Label>
              <Select value={responseTime} onValueChange={setResponseTime}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">Instant (0-1s)</SelectItem>
                  <SelectItem value="quick">Quick (1-3s)</SelectItem>
                  <SelectItem value="natural">Natural (3-5s)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="leadQualification">Lead Qualification</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically qualify leads based on criteria
                  </p>
                </div>
                <Switch
                  id="leadQualification"
                  checked={leadQualification}
                  onCheckedChange={setLeadQualification}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="autoFollowUp">Auto Follow-up</Label>
                  <p className="text-sm text-muted-foreground">
                    Send automatic follow-up messages
                  </p>
                </div>
                <Switch
                  id="autoFollowUp"
                  checked={autoFollowUp}
                  onCheckedChange={setAutoFollowUp}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="businessHours">Business Hours Only</Label>
                  <p className="text-sm text-muted-foreground">
                    Only respond during business hours
                  </p>
                </div>
                <Switch
                  id="businessHours"
                  checked={businessHoursOnly}
                  onCheckedChange={setBusinessHoursOnly}
                />
              </div>
            </div>

            <Button onClick={handleSaveSettings} className="w-full mt-4" data-testid="marketing-ai-save-button">
              Save Configuration
            </Button>
          </CardContent>
        </Card>

        {/* Recent Conversations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Recent Conversations
            </CardTitle>
            <CardDescription>
              Latest interactions with visitors
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentConversations.map((conversation) => (
                <motion.div
                  key={conversation.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Bot className="w-5 h-5 text-primary" />
                      <span className="font-semibold">{conversation.visitor}</span>
                    </div>
                    <Badge variant={conversation.status === 'qualified' ? 'default' : 'secondary'}>
                      {conversation.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {conversation.message}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {conversation.timestamp}
                  </div>
                </motion.div>
              ))}
            </div>

            <Button variant="outline" className="w-full mt-4" data-testid="marketing-ai-view-conversations-button">
              View All Conversations
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* AI Capabilities */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            AI Capabilities
          </CardTitle>
          <CardDescription>
            What your AI agent can do
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Property Recommendations</h4>
                <p className="text-sm text-muted-foreground">
                  Suggest properties based on user preferences
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Lead Qualification</h4>
                <p className="text-sm text-muted-foreground">
                  Assess and score leads automatically
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Appointment Booking</h4>
                <p className="text-sm text-muted-foreground">
                  Schedule viewings with agents
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Price Information</h4>
                <p className="text-sm text-muted-foreground">
                  Provide pricing and financing options
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Area Insights</h4>
                <p className="text-sm text-muted-foreground">
                  Share neighborhood information
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Multilingual Support</h4>
                <p className="text-sm text-muted-foreground">
                  Communicate in multiple languages
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alert */}
      {!isEnabled && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">AI Agent is Currently Inactive</h4>
                <p className="text-sm text-muted-foreground">
                  Enable the AI agent to start engaging with website visitors 24/7 and qualifying leads automatically.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
